import { userOnboardingContract } from "@common/contracts/user-onboarding.contract";
import { useMutation } from "@tanstack/react-query";
import { entitiesAccountsQueryKey } from "@queries/entitiesAccounts";
import { userStatusQueryKey } from "@api/user.api";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { queryClient } from "../queryClient";
import { workspaceQueryKeys } from "./workspace.api";
import { organizationQueryKeys } from "./organization.api";

const userOnboardingApiClient = getBackendAPIClient(userOnboardingContract);

async function completeWorkspaceOnboarding(workspaceId: string) {
    await userOnboardingApiClient.completeWorkspaceOnboarding({
        params: {
            workspaceId,
        },
    });
}

async function completeOrganizationOnboarding(organizationId: string) {
    await userOnboardingApiClient.completeOrganizationOnboarding({
        params: {
            organizationId,
        },
    });
}

interface CompleteInviteOnboardingParams {
    workspaceId?: string;
    organizationId?: string;
}

async function completeInviteOnboarding({
    workspaceId,
    organizationId,
}: CompleteInviteOnboardingParams) {
    await userOnboardingApiClient.completeInviteOnboarding({
        body: {
            workspaceId,
            organizationId,
        },
    });
}

export function useCompleteWorkspaceOnboardingMutation() {
    return useMutation({
        mutationFn: async (workspaceId: string) =>
            await completeWorkspaceOnboarding(workspaceId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [userStatusQueryKey],
            });

            queryClient.invalidateQueries({
                queryKey: workspaceQueryKeys.all(),
            });
        },
    });
}

export function useCompleteOrganizationOnboardingMutation() {
    return useMutation({
        mutationFn: async (organizationId: string) =>
            await completeOrganizationOnboarding(organizationId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [userStatusQueryKey],
            });

            queryClient.invalidateQueries({
                queryKey: organizationQueryKeys.all(),
            });

            // The personal unonboarded workspace is deleted server-side as
            // part of completing org onboarding. Drop its cached entities
            // outright instead of invalidating, otherwise active observers
            // (PrimaryFlow / useStepContent) refetch the deleted workspace
            // before AccountantOutro clears workspaceId from scope.
            queryClient.removeQueries({
                queryKey: workspaceQueryKeys.all(),
            });
            queryClient.removeQueries({
                queryKey: [entitiesAccountsQueryKey],
            });
        },
    });
}

interface CompleteInviteOnboardingMutationParams {
    workspaceId?: string | null;
    organizationId?: string | null;
}

export function useCompleteInviteOnboardingMutation() {
    return useMutation({
        mutationFn: async ({
            workspaceId,
            organizationId,
        }: CompleteInviteOnboardingMutationParams) =>
            await completeInviteOnboarding({
                workspaceId: workspaceId ?? undefined,
                organizationId: organizationId ?? undefined,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [userStatusQueryKey],
            });
        },
    });
}
